import { quoteBondingCurveSellFeeExcludedExactIn } from './bonding-curve.js';
import { quoteCpSwapBaseInput } from './cp-swap.js';
import {
  available,
  checkedAddU128,
  isI64,
  isNonNegativeI64,
  isSignedMagnitudeU128,
  isU64,
  unavailable,
} from './integer.js';
import type { OutcomePoolQuoteContext } from './outcome-pool.js';
import {
  checkedOutcomePoolQuoteContextFromSnapshot,
  type MarketPoolQuoteSnapshot,
} from './snapshot-context.js';
import type { PoolMathResult, QuoteVenue } from './types.js';
import { resolveQuoteVenue } from './venue.js';

export type DisplayMarkVenue = QuoteVenue | 'terminal';

export interface FullPositionDisplayMarkInput {
  context: OutcomePoolQuoteContext;
  outcomeBalanceD6: bigint;
  claimRightsBalanceD6?: bigint;
  remainingCostBasisGrossD6: bigint;
  realizedPnlGrossD6: bigint;
}

export interface FullPositionDisplayMark {
  valuationModel: 'full_position_display_mark';
  venue: DisplayMarkVenue;
  heldOutcomeAmountD6: bigint;
  markedOutcomeAmountD6: bigint;
  currentValueD6: bigint;
  remainingCostBasisGrossD6: bigint;
  unrealizedPnlD6: bigint;
  realizedPnlGrossD6: bigint;
  totalPnlD6: bigint;
}

export interface ConsolidatedPoolSnapshotDisplayMarkHolding {
  snapshot: MarketPoolQuoteSnapshot;
  expectedMarket: string;
  expectedOutcomeIndex: number;
  expectedOutcomeMint: string;
  outcomeBalanceD6: bigint;
  claimRightsBalanceD6?: bigint;
  remainingCostBasisGrossD6: bigint;
}

export interface ConsolidatedPoolSnapshotDisplayMark {
  valuationModel: 'consolidated_pool_snapshot_display_mark';
  currentValueD6: bigint;
  remainingCostBasisGrossD6: bigint;
  unrealizedPnlD6: bigint;
  poolCount: number;
}

/**
 * Gross, fee-excluded mark for read-only P&L display. The mark uses the exact
 * reserve transition while deliberately ignoring execution eligibility such
 * as lifecycle, conviction, open-time, and swap-status gates.
 */
export function valueFullPositionDisplayMark(
  input: FullPositionDisplayMarkInput,
): PoolMathResult<FullPositionDisplayMark> {
  if (
    !isU64(input.outcomeBalanceD6) ||
    !isNonNegativeI64(input.remainingCostBasisGrossD6) ||
    !isI64(input.realizedPnlGrossD6) ||
    !isU64(input.context.chainClock.slot) ||
    !isU64(input.context.chainClock.unixTimestampSeconds)
  ) {
    return unavailable('invalid_amount');
  }

  const venue = displayVenue(input.context);
  if (!venue.available) return venue;
  const markedOutcomeAmount = displayMarkedOutcomeAmount(
    input.context,
    input.outcomeBalanceD6,
    input.claimRightsBalanceD6,
    venue.value,
  );
  if (!markedOutcomeAmount.available) return markedOutcomeAmount;

  const currentValue = displayCurrentValue(input.context, venue.value, markedOutcomeAmount.value);
  if (!currentValue.available) return currentValue;

  const unrealizedPnlD6 = currentValue.value - input.remainingCostBasisGrossD6;
  const totalPnlD6 = input.realizedPnlGrossD6 + unrealizedPnlD6;
  if (!isSignedMagnitudeU128(unrealizedPnlD6) || !isSignedMagnitudeU128(totalPnlD6)) {
    return unavailable('invalid_amount');
  }

  return available({
    valuationModel: 'full_position_display_mark',
    venue: venue.value,
    heldOutcomeAmountD6: input.outcomeBalanceD6,
    markedOutcomeAmountD6: markedOutcomeAmount.value,
    currentValueD6: currentValue.value,
    remainingCostBasisGrossD6: input.remainingCostBasisGrossD6,
    unrealizedPnlD6,
    realizedPnlGrossD6: input.realizedPnlGrossD6,
    totalPnlD6,
  });
}

/**
 * Consolidates controlled holdings by outcome pool and quotes each pool once
 * using the same eligibility-free gross display mark as a single position.
 */
export function valueConsolidatedPoolSnapshotDisplayMark(
  inputs: readonly ConsolidatedPoolSnapshotDisplayMarkHolding[],
): PoolMathResult<ConsolidatedPoolSnapshotDisplayMark> {
  const pools = new Map<string, ConsolidatedDisplayPool>();
  const snapshotIdentityByMarket = new Map<string, string>();
  let remainingCostBasisGrossD6 = 0n;

  for (const input of inputs) {
    if (!isU64(input.outcomeBalanceD6) || !isNonNegativeI64(input.remainingCostBasisGrossD6)) {
      return unavailable('invalid_amount');
    }
    const checkedContext = checkedOutcomePoolQuoteContextFromSnapshot(
      input.snapshot,
      input.expectedMarket,
      input.expectedOutcomeIndex,
      input.expectedOutcomeMint,
    );
    if (!checkedContext.available) return checkedContext;
    const proof = checkedContext.value;
    const existingSnapshotIdentity = snapshotIdentityByMarket.get(proof.marketIdentity);
    if (
      existingSnapshotIdentity !== undefined &&
      existingSnapshotIdentity !== proof.marketSnapshotIdentity
    ) {
      return unavailable('mixed_pool_snapshot');
    }
    snapshotIdentityByMarket.set(proof.marketIdentity, proof.marketSnapshotIdentity);

    const venue = displayVenue(proof.context);
    if (!venue.available) return venue;
    const markedOutcomeAmount = displayMarkedOutcomeAmount(
      proof.context,
      input.outcomeBalanceD6,
      input.claimRightsBalanceD6,
      venue.value,
    );
    if (!markedOutcomeAmount.available) return markedOutcomeAmount;

    const nextBasis = checkedAddU128(remainingCostBasisGrossD6, input.remainingCostBasisGrossD6);
    if (!nextBasis.available) return nextBasis;
    remainingCostBasisGrossD6 = nextBasis.value;

    const existingPool = pools.get(proof.poolIdentity);
    if (existingPool === undefined) {
      pools.set(proof.poolIdentity, {
        context: proof.context,
        venue: venue.value,
        markedOutcomeAmountD6: markedOutcomeAmount.value,
      });
      continue;
    }
    const consolidatedAmountD6 = existingPool.markedOutcomeAmountD6 + markedOutcomeAmount.value;
    if (!isU64(consolidatedAmountD6)) return unavailable('invalid_amount');
    existingPool.markedOutcomeAmountD6 = consolidatedAmountD6;
  }

  let currentValueD6 = 0n;
  for (const pool of pools.values()) {
    const poolValue = displayCurrentValue(pool.context, pool.venue, pool.markedOutcomeAmountD6);
    if (!poolValue.available) return poolValue;
    const nextValue = checkedAddU128(currentValueD6, poolValue.value);
    if (!nextValue.available) return nextValue;
    currentValueD6 = nextValue.value;
  }

  const unrealizedPnlD6 = currentValueD6 - remainingCostBasisGrossD6;
  if (!isSignedMagnitudeU128(unrealizedPnlD6)) return unavailable('invalid_amount');
  return available({
    valuationModel: 'consolidated_pool_snapshot_display_mark',
    currentValueD6,
    remainingCostBasisGrossD6,
    unrealizedPnlD6,
    poolCount: pools.size,
  });
}

interface ConsolidatedDisplayPool {
  context: OutcomePoolQuoteContext;
  venue: DisplayMarkVenue;
  markedOutcomeAmountD6: bigint;
}

export function displayVenue(context: OutcomePoolQuoteContext): PoolMathResult<DisplayMarkVenue> {
  const executionVenue = resolveQuoteVenue(context.venue);
  if (!executionVenue.available && executionVenue.reason === 'terminal_losing_outcome') {
    return available('terminal');
  }
  // A settled winner whose pool is gone (resolved, not yet graduated) has no
  // reserves to mark against; report that rather than a missing-state error.
  if (
    !executionVenue.available &&
    executionVenue.reason === 'market_not_quotable' &&
    context.poolModel === 'missing'
  ) {
    return unavailable('market_not_quotable');
  }
  if (context.poolModel === 'bonding_curve') return available('bonding_curve');
  if (context.poolModel === 'cp_swap') return available('cp_swap');
  if (context.poolModel === 'stale') return unavailable('stale_pool_state');
  return context.venue.hasCpSwapPool
    ? unavailable('missing_cp_swap_state')
    : unavailable('missing_bonding_curve_state');
}

export function displayMarkedOutcomeAmount(
  context: OutcomePoolQuoteContext,
  outcomeBalanceD6: bigint,
  claimRightsBalanceD6: bigint | undefined,
  venue: DisplayMarkVenue,
): PoolMathResult<bigint> {
  if (venue === 'terminal') return available(0n);
  if (context.poolModel !== 'bonding_curve') return available(outcomeBalanceD6);
  if (claimRightsBalanceD6 === undefined) {
    return unavailable('missing_claim_rights_balance');
  }
  // An out-of-order replay can leave the indexed balance below zero until the
  // burn lands. Flooring it to zero would mark a live position at nothing, so
  // the position is reported unvalued instead.
  if (claimRightsBalanceD6 < 0n) return unavailable('claim_rights_replay_deficit');
  if (!isU64(claimRightsBalanceD6)) return unavailable('invalid_amount');
  return available(minimum(outcomeBalanceD6, claimRightsBalanceD6));
}

export function displayCurrentValue(
  context: OutcomePoolQuoteContext,
  venue: DisplayMarkVenue,
  outcomeAmountD6: bigint,
): PoolMathResult<bigint> {
  if (venue === 'terminal') return available(0n);
  if (outcomeAmountD6 === 0n) return available(0n);
  if (venue === 'bonding_curve') {
    if (context.poolModel !== 'bonding_curve') return unavailable('missing_bonding_curve_state');
    return quoteBondingCurveSellFeeExcludedExactIn({
      state: context.bondingCurve.state,
      outcomeAmountInD6: outcomeAmountD6,
    });
  }
  if (context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  const quote = quoteCpSwapBaseInput({
    inputAmountD6: outcomeAmountD6,
    inputReserveD6: context.cpSwap.outcomeReserveD6,
    outputReserveD6: context.cpSwap.quoteReserveD6,
    tradeFeeRateMillionths: 0n,
    creatorFeeRateMillionths: 0n,
    creatorFeeOnInput: false,
  });
  return quote.available ? available(quote.value.outputAmountD6) : quote;
}

function minimum(left: bigint, right: bigint): bigint {
  return left < right ? left : right;
}
